import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'

import {
  Breadcrumbs,
  Button,
  Icon,
  Tabs,
  useDialog,
} from '@sportsyou/react-dom-ui'
import { Colors } from '@sportsyou/core'
import { Community, Post } from '@sportsyou/api'
import { Checkmark, Trash, X } from '@sportsyou/react-icons'

import PagesPostMetrics from './Metrics'
import usePages from '../UsePagesPortal'
import {
  Content,
  Header,
  Main,
  PendingBanner,
} from '../../../styles/global-styles'
import { ContentHeaderRole } from '../../../components/ContentHeader'
import styled from 'styled-components'

interface Props {
  community?: Community
}

export default function PostDetail(props: Props) {
  const { sendBanner, sendConfirm } = useDialog()
  const navigate = useNavigate()
  const params = useParams()

  const { approvePagePost, deletePagePost, page, getPost, roles } = usePages({
    communityId: params.id,
  })

  const [post, setPost] = useState<Post | undefined>(undefined)

  const handleDeletePost = useCallback(() => {
    sendConfirm({
      confirmText: 'Delete',
      isDestructive: true,
      message:
        'Do you want to delete the post? You will lose all data below once you delete it.',
      title: 'Delete Post?',
      onConfirm: async () => {
        await deletePagePost(params.postId as string)
        navigate(`/pages/${params.id}/posts`)
      },
    })
  }, [deletePagePost, navigate, params.id, params.postId, sendConfirm])

  const handleOnApprovePost = async () => {
    sendConfirm({
      confirmText: 'Approve and Post',
      message: 'This will make the post visible to all followers immediately.',
      title: 'Approve?',
      onConfirm: async () => {
        await approvePagePost(params.postId as string)
        sendBanner({
          message: 'Post approved successfully!',
          status: 'success',
          autoDismiss: true,
        })
        navigate(`/pages/${params.id}/posts`)
      },
    })
  }

  useEffect(() => {
    const loadPost = async () => {
      const currentPost = await getPost(params.postId as string)
      setPost(currentPost)
    }
    loadPost()
  }, [getPost, params.postId])

  if (!params.postId) {
    return <h1>Post not found</h1>
  }

  const breadcrumbTextItemStyle = {
    borderRadius: 20,
    paddingLeft: 12,
    paddingRight: 12,
    color: Colors.BLACK,
    textDecoration: 'none',
  }

  return (
    <StyledMain>
      <Header>
        <Breadcrumbs
          divider={<Icon name='ChevronRight' fill={Colors.BLACK} size={12} />}
          items={[
            {
              style: {
                padding: 10,
                borderRadius: '50%',
              },
              icon: <Icon fill={Colors.BLACK} name='Home' size={18} />,
              onClick: () => {
                navigate('/pages')
              },
              title: 'Pages Home',
            },
            {
              style: breadcrumbTextItemStyle,
              text: `${page?.name}`,
              onClick: () => {
                navigate(`/pages/${params.id}`)
              },
              title: `${page?.name}`,
            },
            {
              style: breadcrumbTextItemStyle,
              text: 'Posts',
              onClick: () => {
                navigate(`/pages/${params.id}/posts`)
              },
              title: 'Posts',
            },
            {
              style: breadcrumbTextItemStyle,
              text: post?.communityPostInfo?.postTitle ?? 'Post Detail',

              title: post?.communityPostInfo?.postTitle ?? 'Post Detail',
            },
          ]}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <ContentHeaderRole>{roles.join(', ')}</ContentHeaderRole>
          {page.followerCount ? (
            <small>{page.followerCount} Followers</small>
          ) : null}
        </div>
      </Header>
      <Content>
        <Tabs index={0} contentStyle={tabsContentStyle}>
          <Tabs.Item title='Metrics'>
            <PagesPostMetrics page={page} postId={params.postId} />
          </Tabs.Item>
          {/* <Tabs.Item title="Moderate">
          <PagesPostModerate postId={params.postId} />
        </Tabs.Item> */}
          <Tabs.Item
            title='Edit'
            onClick={() =>
              navigate(`/pages/${params.id}/posts/${params.postId}/edit`)
            }
          ></Tabs.Item>
          <Tabs.ExtraContent>
            <span
              style={{
                display: 'flex',
              }}
            >
              {post?.scheduledTime &&
              moment(post?.scheduledTime).isAfter(moment()) ? (
                <PendingBanner>
                  <Icon
                    name='Clock'
                    fill={Colors.BLACK}
                    style={{ marginRight: 10 }}
                  />
                  Scheduled to post on{' '}
                  {moment(post?.scheduledTime).format('MM/DD/YYYY hh:mm A')}
                </PendingBanner>
              ) : null}
              {page.canApprovePosts && post?.isPending ? (
                <PendingBanner>
                  <span style={{ marginRight: 10 }}>
                    This post is pending approval.
                  </span>
                  <Button
                    variant='success'
                    aria-label='Approve Post'
                    data-status='success'
                    onClick={handleOnApprovePost}
                    title='Approve Post'
                  >
                    <Checkmark
                      style={{ marginRight: 10 }}
                      fill={Colors.WHITE}
                    />
                    Approve and Post
                  </Button>
                </PendingBanner>
              ) : null}
              {page.canPost ? (
                <Button
                  appearance='ghost'
                  onClick={handleDeletePost}
                  style={{ marginLeft: 10 }}
                  variant='danger'
                >
                  <Trash style={{ marginRight: 10 }} fill={Colors.PUNCH} />
                  Delete Post
                </Button>
              ) : null}
            </span>
          </Tabs.ExtraContent>
        </Tabs>
      </Content>
    </StyledMain>
  )
}

const tabsContentStyle = {
  padding: '20px 0',
}

const StyledMain = styled(Main)`
  padding-bottom: 10px;
`
